import { FC, useRef, useState } from "react";
import Badge from "components-new/Badge";
import { ReactComponent as DotsIcon } from "images/ActionMenuIcon.svg";
import { ReactComponent as DocumentIcon } from "images/documentIcon.svg";
import { ReactComponent as RefreshIcon } from "images/Refresh.svg";
import DropSelect from "components-new/DropSelect";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import styles from "./Table.module.sass";
import { convertDate, isEmpty } from "helpers";
import { useLocation, useParams } from "react-router-dom";
import cn from "classnames";
import { directoryAPI } from "libs/apiClient/Directory";
import { DocumentPatternResponse } from "libs/apiClient/data-contracts";

interface RowProps {
	row: DocumentPatternResponse;
	handleCreateDocument: (row: DocumentPatternResponse) => void;
	getData: () => void;
	openConfirm: (cb: () => void, mode: "remove" | "update") => void;
	error: { read: boolean; edit: { active: boolean; snack(): void }; editItems: boolean };
}

const colorRows = {
	1: "#2A00D0",
	2: "#DD0B31",
	3: "#9E00FF",
	4: "#EA8F06",
	5: "#306DC8",
	6: "#35A440",
	7: "#00B8B8",
	8: "#71d6ef",
	9: "#7f0bdd",
	10: "#ff00f2",
	11: "#f8c57c",
	12: "#c86530",
	13: "#00fd1b",
	14: "#f8e58d",
};

const Row: FC<RowProps> = ({ row, error, openConfirm, getData, handleCreateDocument }) => {
	const [actions, setActions] = useState<boolean>(false);
	const actionsRef = useRef(null);
	const location = useLocation();
	const params = useParams();
	useOutsideAlerter(actionsRef.current, () => setActions(false));

	const downloadDoc = async () => {
		const blob = await directoryAPI.docxTemplatesGetDetail(row.id, { format: "blob" }).then((r) => r.data);
		const href = URL.createObjectURL(blob as any);
		const link = document.createElement("a");
		link.href = href;
		link.setAttribute("download", `${row.fullName || "file"}${!row.fullName ? `.docx` : ""}`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(href);
	};
	const removeTemplate = (id: number) => {
		directoryAPI.docxTemplatesDeleteCreate(id).then(getData);
	};

	return (
		<div className={cn(styles.row, { [styles.outline]: row.id === location.state?.outline })}>
			<div className={styles.name}>
				{!row.organizationId ? <div className={styles.flagIcon} /> : <span />}
				{row.docxName}
			</div>
			<div className={styles.download}>
				<Button onClick={downloadDoc} variant={BUTTON_VARIANTS.TEXT}>
					<DocumentIcon className={styles.documentIcon} /> Скачать
				</Button>
			</div>
			<div>{convertDate(row.updateDate!) || "—"}</div>
			<div>{row.authorId ? `${row.authorId.lastName} ${row.authorId.firstName} ${row.authorId.middleName}` : "—"}</div>
			<div>
				<Badge children={row.docxType.name} color={colorRows[row.docxType.id as keyof typeof colorRows]} />
			</div>
			<div className={styles.actions}>
				{actions && (
					<DropSelect target={actionsRef.current} position="right">
						<div className={styles.drop}>
							<div
								className={isEmpty(row.organizationId!) ? styles.mute_reverse : ""}
								onClick={() => {
									if (params.org !== undefined) {
										if (Number(params.org) === 0 && !error.edit.active) {
											error.edit.snack();
											return;
										} else {
											if (!error.editItems) {
												error.edit.snack();
												return;
											}
										}
									}
									if (!isEmpty(row.organizationId!)) {
										openConfirm(() => {
											removeTemplate(row.id);
										}, "remove");
									}
								}}>
								<p className={styles.flagIcon_small} />
								Использовать системный
							</div>
							<div
								onClick={() => {
									if (params.org !== undefined) {
										if (Number(params.org) === 0 && !error.edit.active) {
											error.edit.snack();
											return;
										} else {
											if (!error.editItems) {
												error.edit.snack();
												return;
											}
										}
									}
									if (row.organizationId === null) {
										openConfirm(() => handleCreateDocument(row), "update");
									} else {
										handleCreateDocument(row);
									}
								}}>
								<RefreshIcon className={styles.refreshIcon} />
								Обновить
							</div>
						</div>
					</DropSelect>
				)}
				<DotsIcon ref={actionsRef} onClick={() => setActions((prevState) => !prevState)} />
			</div>
		</div>
	);
};

export default Row;
